import React, { useState } from 'react';
import { elements } from '@ecosystem/newron-design-system';
import { Entity } from '../../types';
import classNames from 'classnames';
import './heatmap.css';
import { useMobile } from '../../context/mobile/mobile.provider';

const { Icon } = elements;

export enum HeatmapScale {
  neutral = 'neutral',
  positive1 = 'positive1',
  positive2 = 'positive2',
  positive3 = 'positive3',
  positive4 = 'positive4',
  negative1 = 'negative1',
  negative2 = 'negative2',
  negative3 = 'negative3',
  negative4 = 'negative4',
}

export type HeatmapCell = {
  raw: number;
  formatted: string;
  scale: HeatmapScale;
  xName: string;
  yName: string;
  isMissing: boolean;
};

export type HeatmapRow = {
  name: string;
  label: string;
  cells: HeatmapCell[];
};

export type HeatmapColumn = {
  name: string;
  label: string;
};

export type HeatmapInfo = {
  columns: HeatmapColumn[];
  rows: HeatmapRow[];
};

const colorMap: Record<HeatmapScale, string> = {
  neutral: 'bg-white text-default',
  positive1: 'bg-green-100 text-default',
  positive2: 'bg-green-200 text-default',
  positive3: 'bg-green-300 text-default',
  positive4: 'bg-green-400 text-default',
  negative1: 'bg-red-100 text-default',
  negative2: 'bg-red-200 text-white',
  negative3: 'bg-red-300 text-white',
  negative4: 'bg-red-400 text-white',
};

type Props = {
  info: HeatmapInfo;
  onCellClick?: (xValue: Entity, yValue: Entity) => void;
  limitRows?: number;
};
function HeatmapTable({ info, onCellClick, limitRows }: Props) {
  const { columns, rows } = info;
  const { isMobile } = useMobile();
  const handleCellClick = (rowIdx: number, colIdx: number) => {
    const col = columns[colIdx];
    const row = rows[rowIdx];

    onCellClick(
      {
        value: row.name,
        label: row.label,
      },
      {
        value: col.name,
        label: col.label,
      }
    );
  };

  let rowsToShow = rows;
  if (limitRows) {
    rowsToShow = rows.slice(0, limitRows);
  }
  return (
    <div className="relative max-w-full w-full overflow-x-auto rounded-2">
      <table className={classNames({ 'border-collapse-separate': isMobile })}>
        <thead>
          <tr>
            <th
              className={classNames('sticky left-0 bg-white', {
                'heatmap-mobile': isMobile,
                'w-56': !isMobile,
              })}
            />
            {columns.map((col, idx) => (
              <th
                align="left"
                className={`${idx === 0 ? 'font-bold' : ''} text-sm font-normal px-3 py-2 w-40 min-w-40`}
                key={col.name}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowsToShow.map((row, rIdx) => (
            <tr key={row.name} className={`h-11 ${rIdx === 0 ? 'font-bold' : ''}`}>
              <td
                className={classNames('h-11 relative overflow-hidden sticky left-0 bg-white', {
                  'heatmap-mobile': isMobile,
                })}
              >
                <span
                  className={classNames('text-sm max-h-11 px-3 nowrap overflow-ellipses relative', {
                    'text-mobile-ellipses': isMobile,
                  })}
                >
                  {row.label}
                </span>
              </td>
              {row.cells.map((cell, cIdx) => (
                <EntryCell
                  key={`${row.name}_${cell.yName}`}
                  onClick={onCellClick ? () => handleCellClick(rIdx, cIdx) : undefined}
                  cell={cell}
                  isTotals={rIdx === 0 || cIdx === 0}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type CellProps = {
  cell: HeatmapCell;
  isTotals?: boolean;
  onClick?: () => void;
};
function EntryCell({ cell, isTotals, onClick }: CellProps) {
  const [hovered, setHovered] = useState<boolean>(false);
  const { formatted, isMissing, scale } = cell;

  let css = '';
  if (isMissing) {
    css = 'text-inactive';
  } else {
    const bgColor = colorMap[scale];
    css = `${bgColor} ${isTotals ? 'font-bold' : 'font-normal'}`;
  }

  return (
    <td className="h-11">
      {(!onClick || isMissing) && (
        <span className={`px-3 h-11 flex flex-row items-center text-xl ${css}`}>{formatted}</span>
      )}
      {onClick && !isMissing && (
        <button
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={`px-3 h-11 w-full text-left flex flex-row items-center gap-2 cursor-pointer border-b border-transparent ${css} transition-colors hover:bg-white hover:text-brand hover:border-brand`}
          onClick={onClick}
        >
          <span className="flex-1 no-wrap">{formatted}</span>
          <span className={`transition-opacity ${hovered ? 'opacity-100' : 'opacity-0'}`}>
            <Icon color="brand" size="sm">
              <Icon.icons.Expand />
            </Icon>
          </span>
        </button>
      )}
    </td>
  );
}

export { HeatmapTable };
